import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, GripVertical, Calendar as Cal, User, Archive, ExternalLink, Paperclip } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import BulkAssignTaskModal from "@/components/BulkAssignTaskModal";
import TaskDetailDrawer from "@/components/TaskDetailDrawer";

const COLUMNS = [
  { key: "todo", label: "To do" },
  { key: "in_progress", label: "In progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];

const PRIORITY_BADGE = {
  high: "border-rose-200 bg-rose-50 text-rose-700",
  medium: "border-amber-200 bg-amber-50 text-amber-700",
  low: "border-sky-200 bg-sky-50 text-sky-700",
};

export default function EmployeeKanban() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [detail, setDetail] = useState(null); // task currently open in drawer
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_user_id: "", priority: "medium", due_date: "", status: "todo" });

  // Only admin/super_admin/employee can fan-out tasks to multiple people /
  // teams / all interns. Interns keep the quick self-add modal.
  const canBulkAssign = ["admin", "super_admin", "employee"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/employee/tasks${showArchived ? "?include_archived=true" : ""}`);
      setTasks(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showArchived]);

  const archiveTask = async (t) => {
    try {
      await api.put(`/employee/tasks/${t.id}`, { archived: !t.archived });
      setTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, archived: !t.archived } : x).filter((x) => showArchived || !x.archived));
      toast.success(t.archived ? "Task restored" : "Task archived");
    } catch { toast.error("Couldn't update"); }
  };

  const openWorkspace = async (t) => {
    // Open in a new tab so the assigner keeps the task board in view. The
    // workspace is (re)provisioned server-side and then opened via window.open.
    try {
      const r = await api.post(`/employee/tasks/${t.id}/workspace`);
      if (r.data?.guidance_id) {
        window.open(`/dashboard/tasks/${r.data.guidance_id}`, "_blank", "noopener,noreferrer");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open workspace"); }
  };

  // Always include the current user at the top of the assignable list so
  // self-assignment works even before /employee/assignable resolves AND when
  // the API hasn't promoted this user into the staff index yet.
  const assigneeOptions = useMemo(() => {
    const me = user ? [{ user_id: user.user_id, name: `${user.name || "Me"} (me)`, role: user.role }] : [];
    const others = (assignableUsers || []).filter((u) => u.user_id !== user?.user_id);
    return [...me, ...others];
  }, [assignableUsers, user]);

  const openNew = async (presetStatus) => {
    // Default new tasks to self-assignment so the most common case (capture a
    // quick personal todo) needs zero extra clicks.
    setForm((f) => ({
      ...f,
      assignee_user_id: user?.user_id || f.assignee_user_id || "",
      status: presetStatus || f.status || "todo",
    }));
    setShowNew(true);
    if (assignableUsers.length === 0) {
      try {
        const r = await api.get("/employee/assignable");
        setAssignableUsers(r.data || []);
      } catch { /* ignore — `me` fallback covers it */ }
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.assignee_user_id) { toast.error("Title + assignee required"); return; }
    try {
      await api.post("/employee/tasks", form);
      toast.success("Task created");
      setShowNew(false);
      setForm({ title: "", description: "", assignee_user_id: user?.user_id || "", priority: "medium", due_date: "", status: "todo" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create"); }
  };

  const moveTask = async (t, newStatus) => {
    if (t.status === newStatus) return;
    setTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, status: newStatus } : x));
    try { await api.put(`/employee/tasks/${t.id}`, { status: newStatus }); }
    catch { toast.error("Couldn't update"); load(); }
  };

  // Native HTML5 drag-and-drop wiring — keeps zero deps.
  const onDragStart = (e, t) => {
    e.dataTransfer.setData("text/plain", t.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, colKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const t = tasks.find((x) => x.id === id);
    if (t) moveTask(t, colKey);
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/employee/tasks/${t.id}`);
      setTasks((arr) => arr.filter((x) => x.id !== t.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  const byCol = useMemo(() => {
    const m = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    for (const t of tasks) (m[t.status] || m.todo).push(t);
    return m;
  }, [tasks]);

  return (
    <div data-testid="employee-kanban-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EMPLOYEE WORKSPACE</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Task <em className="italic text-kb-primary">kanban</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Tasks assigned to you. Drag cards between columns or use the dropdown to move them.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className={`text-xs kb-mono uppercase tracking-wider px-3 py-1.5 border rounded-sm ${showArchived ? "bg-kb-ink text-white border-kb-ink" : "border-slate-300 text-kb-ink-muted hover:border-kb-ink"}`}
            data-testid="kanban-toggle-archive"
          >
            <Archive className="w-3.5 h-3.5 inline mr-1" /> {showArchived ? "Hide archived" : "Show archived"}
          </button>
          <button onClick={() => openNew()} className="kb-btn-outline text-sm" data-testid="kanban-new-task-btn">
            <Plus className="w-4 h-4" /> Quick add
          </button>
          {canBulkAssign && (
            <button onClick={() => setShowBulk(true)} className="kb-btn-primary text-sm" data-testid="kanban-bulk-assign-btn">
              <Plus className="w-4 h-4" /> Assign task
            </button>
          )}
        </div>
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLUMNS.map((c) => (
            <div
              key={c.key}
              className="bg-slate-50 border border-slate-200 rounded-sm p-3"
              data-testid={`kanban-col-${c.key}`}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, c.key)}
            >
              <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted flex items-center justify-between mb-3 px-1">
                <span>{c.label}</span>
                <div className="flex items-center gap-1">
                  <span className="bg-white border border-slate-200 rounded px-1.5 py-0.5">{byCol[c.key].length}</span>
                  <button
                    type="button"
                    onClick={() => openNew(c.key)}
                    className="bg-white border border-slate-200 hover:border-kb-primary hover:text-kb-primary rounded px-1.5 py-0.5"
                    title="Quick add task"
                    data-testid={`kanban-col-${c.key}-add-btn`}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {byCol[c.key].map((t) => (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, t)}
                    onClick={() => setDetail(t)}
                    className="bg-white p-3 border border-slate-200 rounded-sm shadow-sm hover:shadow-md hover:border-kb-primary transition-all cursor-pointer"
                    data-testid={`kanban-task-${t.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-kb-ink">{t.title}</div>
                        {t.description && (
                          <div className="text-xs text-kb-ink-muted mt-1 kb-task-md line-clamp-6">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.description}</ReactMarkdown>
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] kb-mono uppercase tracking-wider">
                          <span className={`px-1.5 py-0.5 rounded-sm border ${PRIORITY_BADGE[t.priority] || ""}`}>{t.priority}</span>
                          {t.due_date && <span className="text-kb-ink-muted inline-flex items-center gap-1"><Cal className="w-3 h-3" /> {t.due_date}</span>}
                          {t.assigner_name && <span className="text-kb-ink-muted inline-flex items-center gap-1"><User className="w-3 h-3" /> {t.assigner_name}</span>}
                          {Array.isArray(t.attachments) && t.attachments.length > 0 && (
                            <span className="text-kb-ink-muted inline-flex items-center gap-1" title={`${t.attachments.length} attachment${t.attachments.length === 1 ? "" : "s"}`} data-testid={`kanban-task-${t.id}-attachments`}>
                              <Paperclip className="w-3 h-3" /> {t.attachments.length}
                            </span>
                          )}
                        </div>
                        {Array.isArray(t.attachments) && t.attachments.some((a) => (a.kind === "image" || (a.mime || "").startsWith("image/"))) && (
                          <div className="mt-2 flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                            {t.attachments.filter((a) => (a.kind === "image" || (a.mime || "").startsWith("image/"))).slice(0, 3).map((a, i) => (
                              <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block w-10 h-10 border border-slate-200 rounded-sm overflow-hidden">
                                <img src={a.url} alt="" className="w-full h-full object-cover" />
                              </a>
                            ))}
                            {t.attachments.filter((a) => (a.kind === "image" || (a.mime || "").startsWith("image/"))).length > 3 && (
                              <span className="w-10 h-10 border border-slate-200 rounded-sm flex items-center justify-center text-[10px] kb-mono text-kb-ink-muted">+{t.attachments.filter((a) => (a.kind === "image" || (a.mime || "").startsWith("image/"))).length - 3}</span>
                            )}
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <select value={t.status} onChange={(e) => moveTask(t, e.target.value)}
                            className="kb-input text-[11px] py-1 px-2 flex-1" data-testid={`kanban-task-${t.id}-status`}>
                            {COLUMNS.map((cc) => <option key={cc.key} value={cc.key}>{cc.label}</option>)}
                          </select>
                          <button type="button" onClick={() => openWorkspace(t)} className="text-kb-primary hover:text-kb-ink p-1" title="Open workspace" data-testid={`kanban-task-${t.id}-open`}>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                          {t.status === "done" && (
                            <button type="button" onClick={() => archiveTask(t)} className="text-kb-ink-muted hover:text-kb-ink p-1" title={t.archived ? "Restore" : "Archive"} data-testid={`kanban-task-${t.id}-archive`}>
                              <Archive className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button onClick={() => deleteTask(t)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`kanban-task-${t.id}-delete`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {byCol[c.key].length === 0 && (
                  <div className="text-center py-6 text-xs text-kb-ink-light italic">No tasks</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-[150] bg-black/40 flex items-center justify-center p-4" data-testid="kanban-new-modal">
          <form onSubmit={createTask} className="bg-white rounded-sm shadow-2xl max-w-md w-full border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-serif text-lg text-kb-ink">Assign new task</h3>
              <button type="button" onClick={() => setShowNew(false)} className="p-1 text-kb-ink-muted hover:text-kb-ink"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Title *</label>
                <input required className="kb-input mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kanban-new-title" />
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Assignee *</label>
                <select required className="kb-input mt-1" value={form.assignee_user_id} onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })} data-testid="kanban-new-assignee">
                  {assigneeOptions.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}{u.employee_role || u.role ? ` · ${u.employee_role || u.role}` : ""}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Column</label>
                  <select className="kb-input mt-1" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="kanban-new-status">
                    {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Priority</label>
                  <select className="kb-input mt-1" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Due date</label>
                  <input type="date" className="kb-input mt-1" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Description</label>
                <textarea rows={3} className="kb-input mt-1 resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className="kb-btn-outline text-xs">Cancel</button>
              <button type="submit" className="kb-btn-primary text-xs" data-testid="kanban-new-submit">Create</button>
            </div>
          </form>
        </div>
      )}

      {showBulk && (
        <BulkAssignTaskModal
          open={showBulk}
          onClose={() => setShowBulk(false)}
          onCreated={load}
          currentUser={user}
          restrictToInterns={user?.role === "employee"}
        />
      )}

      <TaskDetailDrawer
        task={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
        onOpenWorkspace={(t) => { setDetail(null); openWorkspace(t); }}
      />
    </div>
  );
}
